// Referral links (mediasfu.com/signup?ref=CODE, Oct 13 2026): remember the code from the landing URL so the Credits
// page can offer it after signup. Only a convenience: the referral counts once the new account enters it there.
//
// The same landing capture also keeps the first marketing channel (utm_*) a visitor arrived with: MCP listings,
// llms.txt, starter templates, n8n, Make, Zapier (DSFU2/docs/strategy/growth-distribution.md 5.5). The dashboard sends
// it once after signup with sendAcquisitionOnce(). Channel codes never use ?ref=, which stays for referral codes.
const KEY = 'mediasfuReferralCode';
const CODE = /^[A-Za-z0-9]{8}$/;
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export function captureReferralFromUrl(search = typeof window !== 'undefined' ? window.location.search : '') {
  captureAcquisitionFromUrl({ search });
  try {
    const code = new URLSearchParams(search).get('ref');
    if (!code || !CODE.test(code)) return null;
    window.localStorage.setItem(KEY, JSON.stringify({ code: code.toUpperCase(), at: Date.now() }));
    return code.toUpperCase();
  } catch (_) {
    return null;
  }
}

export function storedReferralCode(now = Date.now()) {
  try {
    const saved = JSON.parse(window.localStorage.getItem(KEY) || 'null');
    if (!saved || !CODE.test(saved.code) || now - saved.at > MAX_AGE_MS) return '';
    return saved.code;
  } catch (_) {
    return '';
  }
}

export function clearStoredReferralCode() {
  try { window.localStorage.removeItem(KEY); } catch (_) { /* storage unavailable */ }
}

// ── First-touch channel attribution ──────────────────────────────────────────
// Same storage key and rules as the docs portal (Projecte/website/src/clientModules/acquisitionCapture.ts); both run
// on the mediasfu.com origin. First write wins for 30 days. MediaBack: POST /api/account/acquisition.
const ACQUISITION_KEY = 'mediasfuAcquisition';
const SLUG = /^[a-z0-9][a-z0-9._-]{0,63}$/;

const channelSlug = (value, max = 64) => {
  const text = String(value || '').trim().toLowerCase().slice(0, max);
  return SLUG.test(text) ? text : '';
};

function readAcquisition(now) {
  try {
    const saved = JSON.parse(window.localStorage.getItem(ACQUISITION_KEY) || 'null');
    if (!saved || !saved.source || now - saved.firstSeenAt > MAX_AGE_MS) return null;
    return saved;
  } catch (_) {
    return null;
  }
}

export function captureAcquisitionFromUrl({
  search = typeof window !== 'undefined' ? window.location?.search ?? '' : '',
  pathname = typeof window !== 'undefined' ? window.location?.pathname ?? '/' : '/',
  referrer = typeof document !== 'undefined' ? document.referrer : '',
  now = Date.now(),
} = {}) {
  try {
    if (readAcquisition(now)) return null;
    const params = new URLSearchParams(search);
    const source = channelSlug(params.get('utm_source'));
    if (!source) return null;
    let referrerHost = '';
    try { referrerHost = referrer ? new URL(referrer).hostname.toLowerCase() : ''; } catch (_) { referrerHost = ''; }
    const record = {
      source,
      medium: channelSlug(params.get('utm_medium')),
      campaign: channelSlug(params.get('utm_campaign'), 96),
      content: String(params.get('utm_content') || '').slice(0, 96),
      term: String(params.get('utm_term') || '').slice(0, 96),
      landingPath: String(pathname || '/').slice(0, 200),
      referrerHost,
      firstSeenAt: now,
      sent: false,
    };
    window.localStorage.setItem(ACQUISITION_KEY, JSON.stringify(record));
    return record;
  } catch (_) {
    return null;
  }
}

export function pendingAcquisition(now = Date.now()) {
  const saved = readAcquisition(now);
  return saved && !saved.sent ? saved : null;
}

// Safe to call on every dashboard load: does nothing when nothing is pending, and keeps the record for a later try
// when the server isn't ready (for example before MediaBack has the route).
export async function sendAcquisitionOnce({ apiRoot, token, fetchImpl = typeof fetch !== 'undefined' ? fetch : null, now = Date.now() }) {
  const pending = pendingAcquisition(now);
  if (!pending || !token || !fetchImpl) return false;
  try {
    const { sent, ...body } = pending;
    const response = await fetchImpl(`${apiRoot}api/account/acquisition`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!response.ok) return false;
    window.localStorage.setItem(ACQUISITION_KEY, JSON.stringify({ ...pending, sent: true }));
    return true;
  } catch (_) {
    return false;
  }
}
